import { put, select } from "redux-saga/effects";
import api from "../../../../utils/apiService";
import { ENDPOINT } from "../../../../config/endpoints";
import { METHODS } from "../../../../data/constants";
import {
  getCustomerSummaryRecord,
  authenticateCustomerDataSuccess,
  authenticateCustomerDataFailure,
  customerRecordNotFound,
  getCustomerRecord,
  setExternalLoyaltyData,
} from "../../../slices/CustomerSlice";
import { selectActiveLoyaltyProvider, selectLoyaltyChannelDefId } from "../../../../loyalty/selectors";

/**
 * `redux-store/saga/pos/customer` — the COMMON half.
 *
 * FOUR of the five workers live here as of 2026-09-05; only `saveCustomerData` and the two
 * `root`s still differ.
 * The folder mirrors the SOURCE FILE (`saga/pos/customer.js`), which is why `saga/pos` is three
 * sibling folders rather than one — each app file has its own default export and rootSaga
 * imports all three separately.
 *
 * 🔴 **`getCustomerInfo` and `getDetailedCustomer` were per-platform until 2026-09-05.** Mobile
 * took the `packages/core/loyalty` consent flow, which reads `externalLoyaltyData`, and both
 * workers are what write it — so mobile needed web's behaviour, not its own. **The bodies below
 * are web's**, plus two strictly ADDITIVE allowances for this app's existing callers, each inert
 * on the other platform:
 *   - `getDetailedCustomer` accepts `{ id }` as well as `{ ref }` (mobile's
 *     `fetchDetailedCustomerInfo` sends `id`; web only ever sends `ref`, so `ref ?? id` is `ref`);
 *   - `getDetailedCustomer` invokes `payload.callback` (mobile passes one; web passes none, so
 *     the optional call is a no-op).
 *
 * ⚠️ **What that CHANGED for mobile**, deliberately, because the consent flow needs it: a
 * successful customer search now makes a second request for the full record and dispatches ONE
 * complete `getCustomerRecord` (web's comment: it avoids a double-render of the form), and both
 * workers now write `externalLoyaltyData`. Mobile's `getCustomerRecord` reducer was adjusted to
 * prefer the PAYLOAD's `redemptionScheme` / `membershipName` over the stored record — see the
 * note there; without it this worker's freshly-attached values were discarded.
 *
 * What still differs:
 *   1. `saveCustomerData` — both build the body with the shared `prepareCommonCustomerData`,
 *      but the action payloads differ, and web adds consent
 *      handling plus `skipGlobalLoader`.
 *   2. `root` — the watcher lists.
 *
 * ⚠️ **A rule-2(a) judgement, recorded so it can be overruled:** web's `fetchCustomerSummary`
 * carried three `console.log`s and mobile's carried none — that was the ONLY difference between
 * the two bodies. They are dropped and the body shared, the same call the `filterRemoteStore`
 * precedent made. If they should stay, they belong in `index.desktop.ts` with its own copy.
 *
 * ⚠️ Bare folder imports throughout, as a `shared.ts` must (hazard 16).
 */

export function* fetchCustomerSummary(action: any): any {
  let refId = action.payload;
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.POS.getCustomerSummary(refId),
    });
    yield put(getCustomerSummaryRecord(response));
  } catch (error) {
    console.log('error', error);
  }
}

export function* authenticateCustomer(action: any): any {
  try {
    const response = yield api.request({
      method: 'POST',
      url: ENDPOINT.POS.authenticateCustomer(),
      data: action.payload,
    });
    yield put(authenticateCustomerDataSuccess(response));
  } catch (error) {
    console.log('error', error);
    yield put(authenticateCustomerDataFailure());
  }
}

/* ── shared 2026-09-05: web's bodies, which mobile's consent flow needs ─────────────── */

export function* getCustomerInfo(action: any): any {
  try {
    // Scoped to the same channel the loyalty flow reads the provider and programme under — the
    // bill's own, else the selected one. This used to be a local copy with the precedence reversed,
    // so on a saved bill it asked for the picker's channel while the flow read the bill's.
    const channelDefId = yield select(selectLoyaltyChannelDefId);
    const response = yield api.request({
      method: 'POST',
      url: ENDPOINT.POS.getCustomerInfo(),
      data: action.payload.data
    });
    const activeProvider = yield select(selectActiveLoyaltyProvider);

    let customerPhoneNumber = action.payload.data?.phoneNumber;
    if (response?.results?.length) {
      const customerData = response?.results?.[0];
      const customerRefId = customerData?.customerRefId;
      customerPhoneNumber = customerData?.contactInfo?.phoneNumber;
      // Fetch full customer detail using customerRefId from the filter response.
      // Dispatching once with complete data avoids a double-render of the form.
      const detailResponse = yield api.request({
        method: METHODS.GET,
        url: ENDPOINT.POS.getDetailedCustomerInfo(customerRefId, { channelDefId }),
      });
      if (detailResponse && !detailResponse?.code) {
        yield put(getCustomerRecord({
          ...detailResponse,
          redemptionScheme: customerData?.redemptionScheme,
          membershipName: customerData?.membershipName
        }));
        yield put(setExternalLoyaltyData({
          loyaltyInfo: detailResponse.loyaltyInfo,
          providerConsents: detailResponse.providerConsents,
          // Stamps WHOSE loyalty data this is. `ensureCustomerLoyaltyInfo` needs the identity,
          // not just the presence — `externalLoyaltyData` outlives a customer swap.
          customerRefId
        }));
      } else {
        yield put(getCustomerRecord(customerData));
      }
    } else {
      if (customerPhoneNumber && activeProvider) {
        try {
          const externalResponse = yield api.request({
            method: METHODS.GET,
            url: ENDPOINT.POS.getDetailedCustomerInfo(customerPhoneNumber, { external: true, channelDefId }),
          });
          // The customer is absent from our DB but may exist at the provider, in which
          // case the response carries their details in the same shape as the non-external
          // fetch. Keep them so the new-customer form can be prefilled — deliberately not
          // put into customerInfo, which must stay empty to preserve the new-customer flow.
          const { loyaltyInfo, providerConsents, ...externalCustomer } = externalResponse || {};
          const isCustomerRecord = !externalResponse?.code && !!externalResponse?.contactInfo;
          yield put(setExternalLoyaltyData({
            loyaltyInfo,
            providerConsents,
            customer: isCustomerRecord ? externalCustomer : null
          }));
        } catch (e) {
          // external fetch failed — externalLoyaltyData stays null
        }
      }
      yield put(customerRecordNotFound());
    }
    typeof action.payload.callback === 'function' && action.payload.callback(response);
  } catch (error) {
    console.log('error', error);
  }
}

export function* getDetailedCustomer(action: any): any {
  try {
    // Every caller spells the customer `ref`: `apps/web`, `apps/mobile`'s
    // `fetchDetailedCustomerInfo`, and `packages/core/loyalty`'s `refreshCustomerLoyalty`
    // (which adds `external: true` after each consent success). Mobile sent `id` until
    // 2026-09-05 and this worker briefly accepted both; the call site was changed instead, so
    // there is one spelling again.
    //
    // ⚠️ `callback` IS load-bearing and is NOT a leftover: mobile's customer form is
    // react-hook-form and hydrates its fields from this response
    // (`handleExistingCustomerFormValues` at three call sites). The store update alone does not
    // fill the form there. Web passes no callback, so the optional call is a no-op for it.
    const { ref, external = false, callback } = action?.payload || {};
    const channelDefId = yield select(selectLoyaltyChannelDefId);
    const response = yield api.request({
      method: METHODS.GET,
      url: ENDPOINT.POS.getDetailedCustomerInfo(ref, { external, channelDefId }),
    });
    if (!response?.code) {
      // An external re-read must not overwrite the record we hold: it is the PROVIDER's view of
      // the customer, not ours.
      if (!external) {
        yield put(getCustomerRecord(response));
      }
      yield put(setExternalLoyaltyData({
        loyaltyInfo: response.loyaltyInfo,
        providerConsents: response.providerConsents,
        // ⚠️ Only on the INTERNAL branch. On an external read `ref` is the customer's phone
        // number at the provider, not a `customerRefId`, and stamping it would make
        // `ensureCustomerLoyaltyInfo` believe it holds data for a customer it does not.
        customerRefId: external ? undefined : ref
      }));
      callback?.(response);
    }
  } catch (error) {
    console.log("error", error);
  }
}
